import { Injectable } from '@angular/core';
import { AsamcoAuthService, CommonServices } from '@asamco/base';
import { fixOdataDateOffsetFilter, pathJoin, showErrorSwal, skipTenantInterceptor } from '@asamco/utils';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import notify from 'devextreme/ui/notify';
import { from, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, concatMap, switchMap, tap } from 'rxjs/operators';
import swal from 'sweetalert2';

import { Company, companyDefaults } from '../company/company.interface';
import { ConnectedApp } from '../connected-app/connected-app.interface';
import { ApiUser } from '../interfaces/api-user.interface';
import { CopyDatabase } from '../interfaces/copy-database.interface';
import { NewDatabaseValues } from '../interfaces/new-database-values.interface';
import { CompanyApiSettings } from './company-api-settings.interface';
import { CompanyApiStatus } from './company-api-status.interface';

@Injectable({
	providedIn: 'root',
})
export class CompanyService {
	currentData: Company[] = [];

	currentData$ = new ReplaySubject<Company[]>(1);

	constructor(private cs: CommonServices, private auth: AsamcoAuthService) {}

	getAll(organisationId: number) {
		return this.cs.http.get(`company/fororganisation/${organisationId}`, { headers: skipTenantInterceptor() }).pipe(
			concatMap((result: any[]) => {
				this.currentData = result.map(r => Object.assign(companyDefaults(), r));
				this.currentData$.next(this.currentData);
				return this.currentData$;
			})
		);
	}

	getActiveCompanies(organisationId: number) {
		return this.cs.http.get<Company[]>(`company/fororganisation/${organisationId}/active`, { headers: skipTenantInterceptor() });
	}

	getCompanyStatusses(): Observable<any> {
		return this.cs.http.get('company/status', { headers: skipTenantInterceptor() });
	}

	getCompanyApiStatus(companyId: number): Observable<CompanyApiStatus> {
		return this.cs.http.get<CompanyApiStatus>(`company/${companyId}/apistatus`, { headers: skipTenantInterceptor() });
	}

	getDetailedStatus(companyId: number): Observable<any> {
		return this.cs.http.get(`company/${companyId}/detailedstatus`, { headers: skipTenantInterceptor() });
	}

	reinitializeCompanies(): Observable<any> {
		return this.cs.http.post('company/Reinitialize', null, { headers: skipTenantInterceptor() });
	}

	getCompanyServerInfo(id: number): Observable<any> {
		return this.cs.http.get(`company/${id}/serverinfo`, { headers: skipTenantInterceptor() });
	}

	getCompanyDatabaseActionLogs(id: number): Observable<any> {
		return this.cs.http.get(`company/${id}/database/actionlog`, { headers: skipTenantInterceptor() });
	}

	getCompanyServerInfoInstance(id: number): Observable<any> {
		return this.cs.http.get(`company/${id}/serverinfo/instance`, { headers: skipTenantInterceptor() });
	}

	killCompanyConnector(id: number): Observable<any> {
		return this.cs.http.post(`company/${id}/kill`, null, { headers: skipTenantInterceptor() });
	}

	testCompanyConnector(settings: CompanyApiSettings): Observable<any> {
		return this.cs.http.post(`company/verify`, settings, { headers: skipTenantInterceptor() });
	}

	getApiKeys(id: number): Observable<any> {
		return this.cs.http.get(`company/${id}/remote/apikeys`, { headers: skipTenantInterceptor() });
	}

	viewApiKey(companyId: number, apiKeyId: number): Observable<any> {
		return this.cs.http.get(`company/${companyId}/remote/apikey/${apiKeyId}`, { headers: skipTenantInterceptor() });
	}

	createApiKey(companyId: number, createApiKeyDto): Observable<string> {
		return this.cs.http.post<string>(`company/${companyId}/Remote/ApiKey`, createApiKeyDto, { headers: skipTenantInterceptor() });
	}

	createApiUser(companyId: number, createApiUserDto: ApiUser): Observable<string> {
		return this.cs.http.post<string>(`company/${companyId}/Remote/ApiUser`, createApiUserDto, { headers: skipTenantInterceptor() });
	}

	getApiKeyUsers(companyId: number, databaseId: string): Observable<any> {
		return this.cs.http.get(`company/${companyId}/remote/apikey/users/${databaseId}`, { headers: skipTenantInterceptor() });
	}

	getAllForOrganisation(organisationId: number): Observable<Array<Company>> {
		return this.cs.http.get<Array<Company>>(`company/allfororganisation/${organisationId}`, { headers: skipTenantInterceptor() });
	}

	getAllWithConnectedApps(organisationId: number) {
		return this.cs.http.get<ConnectedApp[]>(`company/allowedconnectedappswithcompany/${organisationId}`);
	}

	get(companyId: number) {
		return <Observable<Company>>this.cs.http.get(`company/${companyId}`, { headers: skipTenantInterceptor() });
	}

	update(company: Company) {
		return this.cs.http.post(`company/Update`, company).pipe(
			tap(id => {
				company.id = <number>id;
				this.currentData.splice(
					this.currentData.findIndex(d => d.id === company.id),
					1,
					company
				);
				this.currentData$.next(this.currentData);
			})
		);
	}

	/**
	 * Saves an Company
	 * @param organisation the saved organisation
	 */
	public save(company: Company) {
		return this.cs.http.post(`company`, company).pipe(
			tap((returnedCompany: Company) => {
				company.id = returnedCompany.id;
				this.currentData.splice(
					this.currentData.findIndex(d => d.id === company.id),
					1,
					company
				);
				this.currentData$.next(this.currentData);
			})
		);
	}

	/**
	 * Authorizes the users for a company
	 * @param users
	 * @param companyId
	 */
	public grantAccess(userIds: number[], companyId: number) {
		return this.cs.http.post('usercompanyaccess/grantaccess', { users: userIds, itemId: companyId });
	}

	/**
	 * Revokes access from a user
	 * @param userId
	 * @param companyId
	 */
	public revokeAccess(userId: number, companyId: number) {
		return this.cs.http.post('usercompanyaccess/revokeaccess', { userId: userId, itemId: companyId });
	}

	delete(companyId: number) {
		return from(
			swal.fire({
				icon: 'warning',
				title: this.cs.ts.translate('SWAL_TITLE.WARNING'),
				html: this.cs.ts.translate('COMPANY.DIALOG.DELETE_QUESTION'),
				showCancelButton: true,
			})
		).pipe(
			switchMap(result => (result.value ? this.cs.http.delete<Company>(`company/${companyId}`) : from([]))),
			tap(company => {
				this.currentData.splice(
					this.currentData.findIndex(d => d.id === company.id),
					1
				);
				this.currentData$.next(this.currentData);
				notify(this.cs.ts.translate('COMPANY.DIALOG.DELETED'), 'success');
			}),
			catchError(error => {
				showErrorSwal(error);
				return of(error);
			})
		);
	}

	public createDatabase(companyId: number, values: NewDatabaseValues): Observable<void> {
		return this.cs.http.post<void>(`company/${companyId}/database`, values);
	}

	public copyDatabase(companyId: number, values: CopyDatabase): Observable<any> {
		return this.cs.http.post(`company/${companyId}/database/copy`, values);
	}

	public getCompanyUsersOdataUrl(companyId: number, withAccess: boolean) {
		return pathJoin([
			this.auth.userSession.backend.odataBaseUrl,
			`CompanyUsers/Company(CompanyId=${companyId},UsersWithAccess=${withAccess})`,
		]);
	}

	public getCompanyUsersOdataDataSource(companyId: number, usersWithAccess: boolean): DataSource {
		return new DataSource({
			paginate: true,
			requireTotalCount: true,
			store: new ODataStore({
				version: 4,
				url: this.getCompanyUsersOdataUrl(companyId, usersWithAccess),
				beforeSend: (request: { params: Object; headers: Object }) => {
					this.auth.dxStoreBeforeSend(request);
				},
				deserializeDates: false,
				onLoading: options => {
					fixOdataDateOffsetFilter(options.filter);
				},
			}),
		});
	}
}
